import { TierProFilled } from '@heidi/design-system/icons';
import { Link } from '@heidi/design-system/link';
import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

import { getCommerceBillingUiPorts } from '../../settings/billing/billing-ui-ports';
import { ComplimentaryTrialEndDialog } from '../complimentary-trial-end/complimentary-trial-end-dialog';
import { ComplimentaryTrialEndOfferCard } from '../complimentary-trial-end/complimentary-trial-end-offer-card';
import { ClinicianTrialEndedUsageClass } from './classify-clinician-trial-usage';
import { clinicianTrialEndedModalMessages as messages } from './clinician-trial-end.messages';

export type Heidi2ClinicianTrialEndedModalProps = {
  open: boolean;
  usageClass: ClinicianTrialEndedUsageClass;
  usageLimitsHref: string;
  /** The yearly Max offer, shown only for usage past the Standard limit. */
  offer?: { cycleEnd: string; conditionsHref: string } | null;
  onUpgrade: () => void;
  onUseFree: () => void;
  onDismiss: () => void;
};

/**
 * Shown once a new user's Clinician trial has ended. Usage past the Clinician
 * Standard limit is sold Clinician Max, with the offer; everyone else the
 * Standard seat. Presentational: the host owns the queue, analytics and routing.
 */
export const Heidi2ClinicianTrialEndedModal = ({
  open,
  usageClass,
  usageLimitsHref,
  offer,
  onUpgrade,
  onUseFree,
  onDismiss,
}: Heidi2ClinicianTrialEndedModalProps) => {
  if (getCommerceBillingUiPorts().isAppleCompliantBuild()) return null;

  const isMoreThanStandard =
    usageClass === ClinicianTrialEndedUsageClass.MoreThanStandard;
  const usageLimitsLink = (chunks: ReactNode) => (
    <Link
      variant="inline"
      href={usageLimitsHref}
      target="_blank"
      rel="noopener noreferrer"
    >
      {chunks}
    </Link>
  );

  return (
    <ComplimentaryTrialEndDialog
      open={open}
      onDismiss={onDismiss}
      testId="clinician-trial-ended-modal"
      icon={<TierProFilled />}
      title={<FormattedMessage {...messages.title} />}
      description={
        <FormattedMessage
          {...(isMoreThanStandard
            ? messages.bodyMoreThanStandard
            : messages.bodyWithinStandard)}
        />
      }
      {...(isMoreThanStandard &&
        offer != null && {
          offer: (
            <ComplimentaryTrialEndOfferCard
              cycleEnd={offer.cycleEnd}
              conditionsHref={offer.conditionsHref}
            />
          ),
        })}
      primaryAction={{
        label: <FormattedMessage {...messages.keepUsingClinician} />,
        onClick: onUpgrade,
        testId: 'clinician-trial-ended-modal-upgrade',
      }}
      secondaryAction={{
        label: <FormattedMessage {...messages.downgradeToFree} />,
        onClick: onUseFree,
        testId: 'clinician-trial-ended-modal-use-free',
      }}
      footnote={
        <FormattedMessage
          {...messages.footnote}
          values={{ usageLimits: usageLimitsLink }}
        />
      }
    />
  );
};
